import useOpenState from '../../../../hooks/useOpenState';
import {ctxDialogTypeOpen, ctxPatientType, setContextDialogTypeOpen} from '../../../../store/reducers/patientContext';
import {useDispatch, useSelector} from 'react-redux';
import {useTranslation} from 'react-i18next';
import InformationBox from '../../../../components/other/informationBox';
import React, {useEffect, useState} from 'react';
import AgyleDialog from '../../../../components/modal/agyleDialog';
import AgyleButton from '../../../../components/ui/buttons/agyleButton';
import {setSnackbar} from '../../../../store/reducers/globals';
import {addWorkflowStep, nextWorkflowStep} from '../../../../store/reducers/workflow';
import {getConfig} from '../../../../util/configUtil';
import moment from 'moment';
import {user} from '../../../../store/reducers/user';
import Formatter from '../../../../util/formatter';
import {isPaedsPatient, PATIENT_CONTEXT_TYPE} from '../../../../util/patientContextUtil';
import Notes from '../../../../util/notes';
import {WORKFLOW_OPTIONS} from '../../../../util/workflowUtil';

const PatientContextWarningDialog = ({patient, handleSaveData}) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();

    // User
    const userObject = useSelector(user);
    const tester = Formatter.formatUserFullName(userObject);

    const patientType = useSelector(ctxPatientType);
    const dialogType = useSelector(ctxDialogTypeOpen);
    const [dialogDetails, setDialogDetails] = useState(null);
    const [isWarning, openWarning, closeWarning] = useOpenState(false, true);

    // Configs
    const CAMHSFormLink = getConfig('CAMHSFormLink', '');
    const adultPsychiatryFormLink = getConfig('CAMHSFormLink', '');
    const causeForConcernFormLink = getConfig('causeForConcernFormLink', '');

    useEffect(() => {
        if (dialogType && patientType !== PATIENT_CONTEXT_TYPE.REVIEW) {
            openWarning();
            handleOpenDialogType(dialogType);
        }
    }, [dialogType, patientType]);

    const handleOpenDialogType = (type) => {
        switch (type) {
            case 'openSafeguardingDialog':
                openSafeguarding();
                break;
            case 'openCauseForConcernDialog':
                openCauseForConcern();
                break;
            case 'openCFSDialog':
                openCFS();
                break;
            case 'openMentalHealthDialog':
                openMentalHealth();
                break;
            default:
                setDialogDetails(null);
                dispatch(setSnackbar({type: 'error', message: 'Wrong workflow type.'}));
                console.error('Workflow type', type);
        }
    };

    const openSafeguarding = () => {
        setDialogDetails({
            title: t('PLEASE COMPLETE PAEDIATRIC SAFEGUARDING'),
            typeWarning: 'warning',
            buttons: [
                {
                    title: t('Open form'),
                    event: () => {
                        dispatch(addWorkflowStep([WORKFLOW_OPTIONS.SAFEGUARDING]));
                        dispatch(nextWorkflowStep());
                    },
                },
                {
                    title: t('Close'),
                    event: () => {
                        dispatch(nextWorkflowStep());
                    },
                },
            ],
        });
    };

    const openCauseForConcern = () => {
        const saveCauseForConcernFormStatus = () => {
            handleSaveData([{
                name: 'studies.causeForConcernForm',
                value: true,
                noteToAdd: Notes.getCauseForConcernFormNote(),
            }]);
            dispatch(nextWorkflowStep());
        };

        setDialogDetails({
            typeWarning: 'info',
            title: t('Please consider filling out Cause for Concern for Adults Referral'),
            buttons: [
                {
                    title: t('Acknowledged'),
                    event: () => {
                        dispatch(nextWorkflowStep());
                    },
                },
                {
                    title: t('Open Link'),
                    event: () => {
                        window.open(causeForConcernFormLink);
                    },
                },
                {
                    title: t('Form already completed'),
                    event: saveCauseForConcernFormStatus,
                },
            ],
        });
    };

    const openCFS = () => {
        const handleCFSNotRequired = () => {
            const data = {
                acknowledge: true,
                timestamp: moment().toISOString(),
                user: tester,
                userLogin: userObject?.userName,
            };

            handleSaveData([{
                name: 'metadata.CFSNotRequired',
                value: data,
                noteToAdd: Notes.getCFSNotRequired(data),
            }]);
            dispatch(nextWorkflowStep());
        };

        setDialogDetails({
            title: t('PLEASE COMPLETE CLINICAL FRAILTY SCORE FORM'),
            typeWarning: 'warning',
            buttons: [
                {
                    title: t('Form not required'),
                    event: () => handleCFSNotRequired(),
                    workflowDisabled: false,
                },
                {
                    title: t('Open form'),
                    event: () => {
                        dispatch(addWorkflowStep([WORKFLOW_OPTIONS.CLINICAL_FRAILTY_SCORE]));
                        dispatch(nextWorkflowStep());
                    },
                },
                {
                    title: t('Close'),
                    event: () => {
                        dispatch(nextWorkflowStep());
                    },
                },
            ],
        });
    };

    const openMentalHealth = () => {
        let isChild = isPaedsPatient(patient.patient.dob);

        const saveMentalHealthFormStatus = (formNotNeeded = false) => {
            handleSaveData([{
                name: 'studies.mentalHealthForm',
                value: true,
                noteToAdd: Notes.getMentalHealthFormNote(isChild, formNotNeeded),
            }]);
            dispatch(nextWorkflowStep());
        };

        setDialogDetails({
            type: 'info',
            title: t(`Please consider filling out ${isChild ? 'CAMHS' : 'Adult Psychiatry'} Referral form`),
            buttons: [
                {
                    title: t('Acknowledged'),
                    event: () => {
                        dispatch(nextWorkflowStep());
                    },
                },
                {
                    title: t('Open Link'),
                    event: () => {
                        window.open(isChild ? CAMHSFormLink : adultPsychiatryFormLink);
                    },
                },
                {
                    title: t('Form Not Needed'),
                    event: () => saveMentalHealthFormStatus(true),
                },
                {
                    title: t('Form already completed'),
                    event: saveMentalHealthFormStatus,
                },
            ],
        });
        openWarning();
    };

    const handleButtonEvent = (event) => {
        closeWarning();
        dispatch(setContextDialogTypeOpen(null));
        event();
    };

    if (!dialogDetails) return null;

    return (
        <AgyleDialog
            open={isWarning}
            disableEscapeKeyDown
            fullWidth
            maxWidth="lg"
            buttons={[
                dialogDetails.buttons.map((button, index) => (
                    <AgyleButton
                        key={'button' + index}
                        onClick={() => handleButtonEvent(button.event)}
                    >
                        {button.title}
                    </AgyleButton>
                )),
            ]}
            disableSave
            disableActionsCloseButton
            disableTitle
        >
            <>
                <InformationBox type={dialogDetails.typeWarning ? dialogDetails.typeWarning : 'warning'}>
                    {dialogDetails.title}
                </InformationBox>
            </>
        </AgyleDialog>
    );
};

export default PatientContextWarningDialog;